import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { AccountAccessShell } from '@/components/account-access-shell';
import { ErrorState } from '@/components/error-state';
import { Button } from '@/components/health-ui';
import { applicationAccessQueryOptions } from '@/hooks/use-application-access-decision';
import { keycloak, useAuth } from '@/lib/auth-guards';
import { isStaffAccount } from '@/lib/human-account';
import router from '@/lib/router';
import { useTRPC } from '@/lib/trpc';

type ApplicationAccessGateProps = {
  children: ReactNode;
};

/** Admits Staff only after a current server-owned assignment decision. */
export function ApplicationAccessGate({
  children,
}: Readonly<ApplicationAccessGateProps>): React.JSX.Element {
  const trpc = useTRPC();
  const { forceTokenRefresh, humanAccountTypes, logout, user } = useAuth();
  const isStaff = isStaffAccount(humanAccountTypes);
  const targetKeycloakUserId = user?.id ?? '';
  const [landingPrepared, setLandingPrepared] = useState(
    () => !isStaff || window.location.pathname !== '/',
  );
  // The one place the application-access query options are built, so this gate
  // and the navigation derivation share a cache entry, an interval and a
  // request (ADR 0328).
  const access = useQuery(
    applicationAccessQueryOptions(trpc, targetKeycloakUserId, isStaff),
  );

  const displayName =
    user?.fullName ??
    user?.email ??
    user?.preferredUsername ??
    user?.username ??
    'Staff account';
  const refreshAccess = async (): Promise<void> => {
    await forceTokenRefresh();
    await access.refetch();
  };

  useEffect(() => {
    if (!isStaff || !access.data?.allowed || landingPrepared) {
      return;
    }
    // Patient Intake is the broader workspace; staff holding both grants land
    // there and reach the QA queue via navigation, not the other way round.
    const landingPath = access.data.patientIntake ? '/referrals' : '/qa';
    let active = true;
    void router.navigate({ to: landingPath, replace: true }).then(() => {
      if (active) {
        setLandingPrepared(true);
      }
    });
    return () => {
      active = false;
    };
  }, [access.data, isStaff, landingPrepared]);

  if (!isStaff) {
    return <>{children}</>;
  }

  if (access.isPending || (access.data?.allowed && !landingPrepared)) {
    return (
      <main
        className="flex min-h-screen items-center justify-center bg-background px-6 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        Checking application access…
      </main>
    );
  }

  if (access.isError) {
    return (
      <main className="min-h-screen bg-background text-foreground">
        <ErrorState
          tone="danger"
          icon={ShieldAlert}
          headline="Unable to verify application access"
          description="Your clinical workspace remains locked because the current access decision could not be loaded. Try again in a moment."
          actions={[
            <Button
              key="retry-access"
              onClick={() => void refreshAccess()}
              disabled={access.isFetching}
            >
              Check access again
            </Button>,
            <Button
              key="sign-out"
              variant="outline"
              onClick={() => void logout()}
            >
              Sign out
            </Button>,
          ]}
          compact={false}
        />
      </main>
    );
  }

  if (!access.data.allowed) {
    return (
      <AccountAccessShell
        displayName={displayName}
        email={user?.email}
        accountUrl={keycloak.createAccountUrl()}
        onRefreshAccess={() => void refreshAccess()}
        onSignOut={() => void logout()}
      />
    );
  }

  return <>{children}</>;
}
